/**
 * Базовый класс для всех игровых событий
 */
export class DomainEvent {
    timestamp;
    eventId;
    eventType;
    constructor() {
        this.timestamp = new Date();
        this.eventId = this.generateEventId();
        this.eventType = this.constructor.name;
    }
    generateEventId() {
        return `${this.constructor.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
}
/**
 * Событие начала игры
 */
export class GameStartedEvent extends DomainEvent {
    players;
    constructor(players) {
        super();
        this.players = players;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            players: Array.from(this.players.entries()),
        };
    }
}
/**
 * Событие хода
 */
export class MoveMadeEvent extends DomainEvent {
    sideId;
    from;
    to;
    round;
    moveInRound;
    constructor(sideId, from, to, round, moveInRound) {
        super();
        this.sideId = sideId;
        this.from = from;
        this.to = to;
        this.round = round;
        this.moveInRound = moveInRound;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            sideId: this.sideId,
            from: this.from.toString(),
            to: this.to.toString(),
            round: this.round,
            moveInRound: this.moveInRound,
        };
    }
}
/**
 * Событие захвата территории
 */
export class TerritoryCapturedEvent extends DomainEvent {
    position;
    previousOwner;
    newOwner;
    captureType;
    wasActiveField;
    groupId;
    groupSize;
    wave;
    // Optional, appended fields (default-safe for existing call sites). They are
    // observational metadata for the tutorial hints — they never affect outcomes:
    //   wasActiveField — the captured tile was the victim's movable "active" field
    //                    (the high-value 30-pt field): drives active-field hints.
    //   groupId/groupSize — identity of the surrounded GROUP this capture belongs
    //                    to, so the client can tell single vs group vs mixed capture.
    //   wave           — for 'end-turn-conquest', the stage (1, 2, …) of
    //                    MoveExecutor.resolveGroupIteratively that took the field,
    //                    so the rules screen can number the stages the engine ran.
    constructor(position, previousOwner, newOwner, captureType, wasActiveField = false, groupId, groupSize, wave) {
        super();
        this.position = position;
        this.previousOwner = previousOwner;
        this.newOwner = newOwner;
        this.captureType = captureType;
        this.wasActiveField = wasActiveField;
        this.groupId = groupId;
        this.groupSize = groupSize;
        this.wave = wave;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            position: this.position.toString(),
            previousOwner: this.previousOwner,
            newOwner: this.newOwner,
            captureType: this.captureType,
            wasActiveField: this.wasActiveField,
            groupId: this.groupId,
            groupSize: this.groupSize,
            wave: this.wave,
        };
    }
}
/**
 * Событие попадания в ловушку
 */
export class TrapTriggeredEvent extends DomainEvent {
    trappedSide;
    position;
    surroundingSides;
    canEscape;
    trapType;
    forced;
    constructor(trappedSide, position, surroundingSides, canEscape, trapType, 
    // Every legal move the side had led into a trap (forced self-encirclement).
    forced = false) {
        super();
        this.trappedSide = trappedSide;
        this.position = position;
        this.surroundingSides = surroundingSides;
        this.canEscape = canEscape;
        this.trapType = trapType;
        this.forced = forced;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            trappedSide: this.trappedSide,
            position: this.position.toString(),
            surroundingSides: this.surroundingSides,
            canEscape: this.canEscape,
            trapType: this.trapType,
            forced: this.forced,
        };
    }
}
/**
 * Событие разрешения ловушки
 */
export class TrapResolvedEvent extends DomainEvent {
    trappedSide;
    position;
    winner;
    resolution;
    // 'escaped' — the side broke the ring before its turn ended, so a deferred
    // trap never resolved; the field stays its own (winner = trappedSide).
    constructor(trappedSide, position, winner, resolution) {
        super();
        this.trappedSide = trappedSide;
        this.position = position;
        this.winner = winner;
        this.resolution = resolution;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            trappedSide: this.trappedSide,
            position: this.position.toString(),
            winner: this.winner,
            resolution: this.resolution,
        };
    }
}
/**
 * Событие элиминации стороны
 */
export class SideEliminatedEvent extends DomainEvent {
    sideId;
    reason;
    byMover;
    toSide;
    //   byMover — the side whose move or turn end was being resolved (for a side
    //             that walked into a trap, itself);
    //   toSide  — the side that took the field it went out on (none when burned).
    constructor(sideId, reason, byMover, toSide) {
        super();
        this.sideId = sideId;
        this.reason = reason;
        this.byMover = byMover;
        this.toSide = toSide;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            sideId: this.sideId,
            reason: this.reason,
            byMover: this.byMover,
            toSide: this.toSide,
        };
    }
}
/**
 * Событие реанимации стороны
 */
export class SideReanimatedEvent extends DomainEvent {
    sideId;
    territory;
    byPlayerId;
    reason;
    // Single canonical reanimation event. Previously a SECOND class with the same
    // name lived in ReanimationManager (the one actually emitted live) — because
    // EventBus dispatches by constructor.name, a subscriber of one received the
    // other and read undefined fields. Unified here; ReanimationManager imports it.
    // reason is a plain string (ReanimationReason values are strings) to avoid a
    // GameEvents -> ReanimationManager import cycle.
    constructor(sideId, territory, byPlayerId, reason = 'player_gift') {
        super();
        this.sideId = sideId;
        this.territory = territory;
        this.byPlayerId = byPlayerId;
        this.reason = reason;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            sideId: this.sideId,
            territory: this.territory.toString(),
            byPlayerId: this.byPlayerId,
            reason: this.reason,
        };
    }
}
/**
 * Событие выжигания территории
 */
export class TerritoryBurnedEvent extends DomainEvent {
    position;
    reason;
    constructor(position, reason) {
        super();
        this.position = position;
        this.reason = reason;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            position: this.position.toString(),
            reason: this.reason,
        };
    }
}
/**
 * Событие начала нового раунда
 */
export class RoundStartedEvent extends DomainEvent {
    round;
    activeSides;
    constructor(round, activeSides) {
        super();
        this.round = round;
        this.activeSides = activeSides;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            round: this.round,
            activeSides: this.activeSides,
        };
    }
}
/**
 * Событие окончания игры
 */
export class GameEndedEvent extends DomainEvent {
    scores;
    winner;
    reason;
    constructor(scores, winner, reason) {
        super();
        this.scores = scores;
        this.winner = winner;
        this.reason = reason;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            scores: Array.from(this.scores.entries()),
            winner: this.winner,
            reason: this.reason,
        };
    }
}
/**
 * Событие изменения статуса игры
 */
export class GameStatusChangedEvent extends DomainEvent {
    oldStatus;
    newStatus;
    constructor(oldStatus, newStatus) {
        super();
        this.oldStatus = oldStatus;
        this.newStatus = newStatus;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            oldStatus: this.oldStatus,
            newStatus: this.newStatus,
        };
    }
}
/**
 * Событие каскадного захвата
 */
export class CascadeCaptureEvent extends DomainEvent {
    initiator;
    capturedTerritories;
    affectedSides;
    constructor(initiator, capturedTerritories, affectedSides) {
        super();
        this.initiator = initiator;
        this.capturedTerritories = capturedTerritories;
        this.affectedSides = affectedSides;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            initiator: this.initiator,
            capturedTerritories: this.capturedTerritories.map(p => p.toString()),
            affectedSides: this.affectedSides,
        };
    }
}
/**
 * Событие полуокружения — сторона в одном ходу от полного окружения.
 * Observational (никак не влияет на ход игры) — только для подсказок туториала.
 */
export class SemiEncirclementEvent extends DomainEvent {
    side;
    openPosition;
    surroundingSides;
    constructor(side, openPosition, // the single remaining escape/neutral cell
    surroundingSides) {
        super();
        this.side = side;
        this.openPosition = openPosition;
        this.surroundingSides = surroundingSides;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            side: this.side,
            openPosition: this.openPosition.toString(),
            surroundingSides: this.surroundingSides,
        };
    }
}
/**
 * Событие пропуска хода стороны
 */
export class TurnSkippedEvent extends DomainEvent {
    sideId;
    reason;
    round;
    skippedMoves;
    constructor(sideId, reason, round, skippedMoves) {
        super();
        this.sideId = sideId;
        this.reason = reason;
        this.round = round;
        this.skippedMoves = skippedMoves;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            sideId: this.sideId,
            reason: this.reason,
            round: this.round,
            skippedMoves: this.skippedMoves,
        };
    }
}
//# sourceMappingURL=GameEvents.js.map